import { CUSTOM_EVENT } from '../config';
import { delegateEvent } from '../utils/events';
import { createFocusTrap } from 'focus-trap';

export default class Modal extends HTMLElement {
  constructor(m) {
    super(m);

    this._name = this.dataset.modalName;
    this._selector = `[data-${this._name}-toggler]`;
    this._isPublicationModal =
      this.id === 'publication-modal' ||
      this.dataset.modalName === 'publication' ||
      Boolean(this.closest('#publication-modal'));

    // Binding
    this.onToggle = this.onToggle.bind(this);
    this.onModalOpen = this.onModalOpen.bind(this);
    this.onVisitStart = this.onVisitStart.bind(this);
    this.onPublicationKeydown = this.onPublicationKeydown.bind(this);

    // UI
    this.$togglers = document.querySelectorAll(`[data-${this._name}-toggler]`);
    this.$focusTrapTargets = Array.from(this.querySelectorAll('[data-target]'));

    // Data
    // Focus trap options
    this.focusTrapOptions = {
      /**
       * There is a delay between when the class is applied
       * and when the element is focusable
       */
      checkCanFocusTrap: trapContainers => {
        const results = trapContainers.map(trapContainer => {
          return new Promise(resolve => {
            const interval = setInterval(() => {
              if (getComputedStyle(trapContainer).visibility !== 'hidden') {
                resolve();
                clearInterval(interval);
              }
            }, 5);
          });
        });

        // Return a promise that resolves when all the trap containers are able to receive focus
        return Promise.all(results);
      },

      onActivate: () => {
        this.classList.add('is-active');
        document.documentElement.classList.add('has-modal-open', `has-${this._name}-open`);
        this.setAttribute('aria-hidden', false);
        this.isOpen = true;

        this.onActivate?.();
      },

      onPostActivate: () => {
        this.$togglers.forEach($toggler => {
          $toggler.setAttribute('aria-expanded', true);
        });
      },

      onDeactivate: () => {
        this.classList.remove('is-active');
        document.documentElement.classList.remove('has-modal-open', `has-${this._name}-open`);
        this.setAttribute('aria-hidden', true);
        this.isOpen = false;

        window.dispatchEvent(new CustomEvent(CUSTOM_EVENT.MODAL_CLOSE, { detail: this }));

        this.onDeactivate?.();
      },

      onPostDeactivate: () => {
        this.$togglers.forEach($toggler => {
          $toggler.setAttribute('aria-expanded', false);
        });
      },

      clickOutsideDeactivates: true,
      initialFocus: this.$focusTrapTargets[0]
    };

    if (this._isPublicationModal) {
      // Esc обрабатывается в Load.closePublicationModal (history.back + fragment visit).
      this.focusTrapOptions.escapeDeactivates = false;
    }

    this.isOpen = false;
  }

  // =============================================================================
  // Lifecycle methods
  // =============================================================================
  connectedCallback() {
    this.bindEvents();

    this.focusTrap = createFocusTrap(
      this.$focusTrapTargets.length > 0 ? this.$focusTrapTargets : this,
      this.focusTrapOptions
    );

    if (this._isPublicationModal) {
      this.addEventListener('keydown', this.onPublicationKeydown);
    }
  }

  disconnectedCallback() {
    this.unbindEvents();

    if (this._isPublicationModal) {
      this.removeEventListener('keydown', this.onPublicationKeydown);
    }
  }

  // =============================================================================
  // Events
  // =============================================================================
  bindEvents() {
    window.addEventListener(CUSTOM_EVENT.VISIT_START, this.onVisitStart);
    window.addEventListener(CUSTOM_EVENT.MODAL_OPEN, this.onModalOpen);

    this.unbindToggleEvent = delegateEvent(document.body, 'click', this._selector, this.onToggle);
  }

  unbindEvents() {
    window.removeEventListener(CUSTOM_EVENT.VISIT_START, this.onVisitStart);
    window.removeEventListener(CUSTOM_EVENT.MODAL_OPEN, this.onModalOpen);

    this.unbindToggleEvent.abort?.();
  }

  // =============================================================================
  // Callbacks
  // =============================================================================
  onToggle(e) {
    if (this.classList.contains('is-active')) {
      this.close(e);
    } else {
      this.open(e);
    }
  }

  onVisitStart() {
    // Close the modal on page change
    this.close();
  }

  onPublicationKeydown(e) {
    if (e.key !== 'Escape' && e.key !== 'Esc' && e.keyCode !== 27) return;
    if (!this.isOpen) return;

    e.preventDefault();
    e.stopPropagation();

    window.dispatchEvent(new CustomEvent(CUSTOM_EVENT.PUBLICATION_MODAL_CLOSE));
  }

  onModalOpen(event) {
    // Close the modal if another one is opened
    if (event.detail !== this) {
      this.close();
    }
  }

  // =============================================================================
  // Methods
  // =============================================================================
  open(args) {
    if (this.isOpen) return;

    this.focusTrap?.activate?.();

    this.onOpen?.(args);

    window.dispatchEvent(new CustomEvent(CUSTOM_EVENT.MODAL_OPEN, { detail: this }));
  }

  close(args) {
    if (!this.isOpen) return;

    this.focusTrap?.deactivate?.();

    window.dispatchEvent(new CustomEvent(CUSTOM_EVENT.MODAL_CLOSE, { detail: this }));

    this.onClose?.(args);
  }

  rebindTogglers() {
    // Called from Load
    this.$togglers = document.querySelectorAll(`[data-${this._name}-toggler]`);
  }
}
